import React, { useState, useEffect, useMemo } from "react";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Pencil, Trash2, Search, Plus, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SituationsManagement = () => {
  const supabase = useSupabase();
  const [situations, setSituations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentSituation, setCurrentSituation] = useState(null);
  const [newSituation, setNewSituation] = useState({
    title: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSituations();
  }, []);

  const fetchSituations = async () => {
    setIsLoading(true);
    setError("");
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) throw new Error("User not found");

      const { data, error } = await supabase
        .from("situations")
        .select(
          `
          *,
          creator:creator_id (
            first_name,
            last_name
          )
        `
        )
        .eq("creator_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSituations(data);
    } catch (error) {
      console.error("Error fetching situations:", error);
      setError("Failed to fetch situations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSituations = useMemo(() => {
    return situations.filter(
      (situation) =>
        situation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        situation.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [situations, searchTerm]);

  const handleAddSituation = async () => {
    setError("");
    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) throw new Error("User not found");

      const { data: situation, error: situationError } = await supabase
        .from("situations")
        .insert({
          ...newSituation,
          creator_id: user.id,
        })
        .select()
        .single();

      if (situationError) throw situationError;

      // Generate AI response using the serverless function
      const aiResponseData = await fetch("/api/generate-ai-response", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: newSituation.description,
        }),
      });

      if (!aiResponseData.ok) {
        const errorData = await aiResponseData.json();
        throw new Error(errorData.error || "Failed to generate AI response");
      }

      const { response: aiResponse } = await aiResponseData.json();

      // Create the first solution with AI response
      const { error: solutionError } = await supabase.from("solutions").insert({
        situation_id: situation.id,
        user_id: null, // Null for AI-generated solution
        content: aiResponse,
      });

      if (solutionError) throw solutionError;

      setSituations([situation, ...situations]);
      setNewSituation({ title: "", description: "" });
      setIsAddDialogOpen(false);
    } catch (err) {
      console.error("Error adding situation:", err);
      setError("Failed to add situation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSituation = async () => {
    setError("");
    try {
      const { data, error } = await supabase
        .from("situations")
        .update({
          title: currentSituation.title,
          description: currentSituation.description,
        })
        .eq("id", currentSituation.id)
        .select();
      if (error) throw error;
      setSituations(
        situations.map((s) => (s.id === currentSituation.id ? data[0] : s))
      );
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error editing situation:", error);
      setError("Failed to edit situation. Please try again.");
    }
  };

  const handleDeleteSituation = async () => {
    setError("");
    try {
      const { error } = await supabase
        .from("situations")
        .delete()
        .eq("id", currentSituation.id);
      if (error) throw error;
      setSituations(situations.filter((s) => s.id !== currentSituation.id));
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting situation:", error);
      setError(
        "Zatiaľ som neimplementoval, musíme si zadefinovať či zmazať zároveň aj všetky hodnotenia."
      );
    }
  };

  const SituationCard = ({ situation }) => (
    <Card className="mb-4 hover:shadow-md transition-shadow duration-300">
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {situation.title}
            </h3>
            <div
              className="text-gray-600 mt-1"
              dangerouslySetInnerHTML={{ __html: situation.description }}
            />
          </div>
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setCurrentSituation(situation);
                setIsEditDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {/* <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setCurrentSituation(situation);
                setIsDeleteDialogOpen(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button> */}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Vyhľadať situáciu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 w-full sm:w-64"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          onClick={() => setIsAddDialogOpen(true)}
          className="w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Pridať Novú Situáciu
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-red-600" />
        </div>
      ) : filteredSituations.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            Nebola nájdená žiadna situácia.
          </CardContent>
        </Card>
      ) : (
        filteredSituations.map((situation) => (
          <SituationCard key={situation.id} situation={situation} />
        ))
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Pridať Novú Situáciu</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Názov</Label>
              <Input
                id="title"
                value={newSituation.title}
                onChange={(e) =>
                  setNewSituation({ ...newSituation, title: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2 pb-8 ">
              <Label htmlFor="description">Popis</Label>
              <ReactQuill
                theme="snow"
                value={newSituation.description}
                onChange={(content) =>
                  setNewSituation({ ...newSituation, description: content })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddSituation} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Pridávam...
                </>
              ) : (
                "Pridať Situáciu"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upraviť Situáciu</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-title">Názov</Label>
              <Input
                id="edit-title"
                value={currentSituation?.title}
                onChange={(e) =>
                  setCurrentSituation({
                    ...currentSituation,
                    title: e.target.value,
                  })
                }
              />
            </div>
            <div className="grid gap-2 pb-8">
              <Label htmlFor="edit-description">Popis</Label>
              <ReactQuill
                theme="snow"
                value={currentSituation?.description}
                onChange={(content) =>
                  setCurrentSituation({
                    ...currentSituation,
                    description: content,
                  })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleEditSituation}>Uložiť Zmeny</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Ste si istý, že chcete vymazať túto situáciu?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Táto akcia sa nedá vrátiť späť. Situácia bude natrvalo odstránená
              z našej databázy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušiť</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSituation}>
              Vymazať
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SituationsManagement;
